import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Briefcase, ArrowLeft, Users, Building } from 'lucide-react';
import PageFooter from '../components/PageFooter';

const Register = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [userType, setUserType] = useState('candidate');
    const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', name: '', company_name: '', sector: '' });
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    // On mobile, the on-screen keyboard can cover the field being typed into
    // (and the submit button below it). Scrolling the focused field toward
    // the top of the viewport keeps it — and the button beneath it — clear
    // of the keyboard once it animates in.
    const scrollFieldAboveKeyboard = (e) => {
        setTimeout(() => {
            e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
    };

    useEffect(() => {
        const typeParam = searchParams.get('type');
        if (typeParam === 'candidate' || typeParam === 'company') {
            setUserType(typeParam);
        }
    }, [searchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (userType === 'company') {
            toast.error('Employer access is coming soon.');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        try {
            const data = { ...formData, user_type: userType };
            if (userType === 'candidate') {
                delete data.company_name;
                delete data.sector;
            }
            const user = await register(data);
            toast.success('Registration successful!');
            
            if (user.user_type === 'company') navigate('/company/dashboard');
            else if (user.user_type === 'candidate') navigate('/candidate/assessment');
        } catch (error) {
            toast.error(error.userMessage || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F0FDF4' }}>
            <Helmet>
                <title>Sampler | Get Started</title>
                <meta name="description" content="Create your Sampler account." />
            </Helmet>
            <div className="flex-1 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <Button variant="ghost" onClick={() => navigate('/')} className="mb-3 sm:mb-6 text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" data-testid="back-btn">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
                </Button>
                
                <div className="bg-white rounded-3xl shadow-sm p-5 sm:p-8 border border-purple-200/50">
                    <div className="flex items-center space-x-2 mb-5 sm:mb-8">
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                    </div>
                    
                    <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Create Account
                    </span>
                    <h2 className="text-3xl font-bold mb-1 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="register-heading">Get Started</h2>
                    <p className="text-slate-500 mb-5 sm:mb-8" style={{ fontFamily: 'Barlow, sans-serif' }}>Create your account</p>

                    {/* Beta banner — remove by setting REACT_APP_BETA_MODE=false */}
                    {process.env.REACT_APP_BETA_MODE === 'true' && (
                        <div className="mb-4 sm:mb-6 px-4 py-3 rounded-xl bg-purple-50 border border-purple-200 text-sm text-purple-700 text-center leading-snug" data-testid="beta-banner" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            This is our candidate beta. Full launch and employer access coming soon!
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 mb-5 sm:mb-8">
                        <Button 
                            variant={userType === 'company' ? 'default' : 'outline'} 
                            onClick={() => setUserType('company')} 
                            className={`rounded-full h-11 ${userType === 'company' ? '' : 'border-purple-200/50 text-slate-600 hover:bg-purple-50 hover:border-purple-300'}`}
                            style={userType === 'company' ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)', color: 'white' } : {}}
                            data-testid="company-type-btn"
                        >
                            <Building className="mr-2 h-4 w-4" /> Company
                        </Button>
                        <Button 
                            variant={userType === 'candidate' ? 'default' : 'outline'} 
                            onClick={() => setUserType('candidate')} 
                            className={`rounded-full h-11 ${userType === 'candidate' ? '' : 'border-purple-200/50 text-slate-600 hover:bg-purple-50 hover:border-purple-300'}`}
                            style={userType === 'candidate' ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)', color: 'white' } : {}}
                            data-testid="candidate-type-btn"
                        >
                            <Users className="mr-2 h-4 w-4" /> Candidate
                        </Button>
                    </div>
                    
                    <div className="relative">
                        {userType === 'company' && (
                            <div className="absolute inset-0 z-10 flex items-start justify-center pt-16" data-testid="company-coming-soon-overlay">
                                <div className="bg-white/95 border border-purple-200 rounded-2xl px-6 py-4 shadow-md text-center max-w-xs">
                                    <p className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Barlow, sans-serif' }} data-testid="company-coming-soon-message">
                                        Employer access — coming soon.
                                    </p>
                                </div>
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className={`space-y-4 ${userType === 'company' ? 'blur-sm pointer-events-none select-none' : ''}`} aria-hidden={userType === 'company'}>
                        <div>
                            <Label htmlFor="email" className="text-slate-700 font-semibold text-sm">Email Address</Label>
                            <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required={userType !== 'company'} disabled={userType === 'company'} className="mt-1.5 rounded-xl border-purple-200/50" data-testid="email-input" />
                        </div>
                        
                        <div>
                            <Label htmlFor="name" className="text-slate-700 font-semibold text-sm">{userType === 'company' ? 'Contact Name' : 'Full Name'}</Label>
                            <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required={userType !== 'company'} disabled={userType === 'company'} className="mt-1.5 rounded-xl border-purple-200/50" data-testid="name-input" />
                        </div>
                        
                        {userType === 'company' && (
                            <>
                                <div>
                                    <Label htmlFor="company_name" className="text-slate-700 font-semibold text-sm">Company Name</Label>
                                    <Input id="company_name" value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} disabled className="mt-1.5 rounded-xl border-purple-200/50" data-testid="company-name-input" />
                                </div>
                                <div>
                                    <Label htmlFor="sector" className="text-slate-700 font-semibold text-sm">Sector</Label>
                                    <Select value={formData.sector} onValueChange={(value) => setFormData({...formData, sector: value})} disabled>
                                        <SelectTrigger className="mt-1.5 w-full h-10 rounded-xl border-purple-200/50 bg-white" data-testid="sector-select">
                                            <SelectValue placeholder="Select sector" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white rounded-xl">
                                            <SelectItem value="property">Property</SelectItem>
                                            <SelectItem value="tech_product">Tech & Product</SelectItem>
                                            <SelectItem value="sales_account">Sales & Account Management</SelectItem>
                                            <SelectItem value="creative_events">Creative & Events</SelectItem>
                                            <SelectItem value="hr_people">HR & People</SelectItem>
                                            <SelectItem value="marketing_growth">Marketing & Growth</SelectItem>
                                            <SelectItem value="operations_admin">Operations & Admin</SelectItem>
                                            <SelectItem value="finance_accounting">Finance & Accounting</SelectItem>
                                            <SelectItem value="legal_compliance">Legal & Compliance</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}
                        
                        <div>
                            <Label htmlFor="password" className="text-slate-700 font-semibold text-sm">Password</Label>
                            <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} onFocus={scrollFieldAboveKeyboard} required={userType !== 'company'} disabled={userType === 'company'} minLength={6} className="mt-1.5 rounded-xl border-purple-200/50" data-testid="password-input" />
                        </div>
                        
                        <div>
                            <Label htmlFor="confirmPassword" className="text-slate-700 font-semibold text-sm">Confirm Password</Label>
                            <Input id="confirmPassword" type="password" value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} onFocus={scrollFieldAboveKeyboard} required={userType !== 'company'} disabled={userType === 'company'} className="mt-1.5 rounded-xl border-purple-200/50" data-testid="confirm-password-input" />
                            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                            )}
                        </div>

                        {/* Terms agreement */}
                        <div className="flex items-start gap-3 pt-1">
                            <input
                                type="checkbox"
                                id="agreeTerms"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                disabled={userType === 'company'}
                                className="mt-0.5 h-4 w-4 rounded border-purple-300 text-[#8B5CF6] accent-[#8B5CF6] cursor-pointer flex-shrink-0"
                                data-testid="agree-terms-checkbox"
                            />
                            <label htmlFor="agreeTerms" className="text-xs text-slate-500 leading-relaxed cursor-pointer" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                I have read and agree to the{' '}
                                <Link to="/legal/candidate-terms" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] font-semibold hover:underline" data-testid="terms-link">
                                    Candidate Terms of Use
                                </Link>
                                {' '}and{' '}
                                <Link to="/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] font-semibold hover:underline" data-testid="privacy-link">
                                    Privacy Policy
                                </Link>
                            </label>
                        </div>
                        
                        <Button type="submit" className="w-full btn-primary h-12" disabled={loading || !agreedToTerms || userType === 'company'} data-testid="register-submit-btn">
                            {loading ? 'Creating account...' : userType === 'company' ? 'Coming soon' : 'Create Account'}
                        </Button>
                        </form>
                    </div>
                    
                    <p className="mt-5 sm:mt-8 text-center text-sm text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        Already have an account? <Link to="/login" className="text-[#8B5CF6] font-semibold hover:underline" data-testid="login-link">Log in</Link>
                    </p>
                </div>
                </div>
            </div>
            <PageFooter />
        </div>
    );
};

export default Register;
